import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter } from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useInvoicesInfinite } from '../hooks/useInvoicesInfinite';
import InvoiceTable from './Invoices/components/InvoiceTable';
import InvoiceFormWizard from './Invoices/components/InvoiceFormWizard';

// TODO(row11-search): Implement server-side full-text search + UI

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, currentBusiness, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false);
  const [modalCustomerId, setModalCustomerId] = useState<string | undefined>(undefined);

  const {
    invoices,
    error: fetchError,
    isLoading,
    isLoadingMore,
    isReachingEnd,
    loadMore,
    mutate,
    isEmpty,
  } = useInvoicesInfinite(searchTerm, statusFilter);

  // Handle refresh parameter from navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('refresh') === 'true') {
      // Remove the refresh parameter from URL
      navigate(location.pathname, { replace: true });
      // Trigger a refresh of the invoices list
      mutate();
    }
  }, [location.search, navigate, mutate]);

  // Transform invoices to match InvoiceTable interface
  const transformedInvoices = useMemo(() => {
    console.log('Raw invoices data:', invoices);
    return invoices.map((invoice: any) => {
      console.log('Processing invoice:', invoice);
      return {
        id: String(invoice.id),
        customer_id: invoice.customer_id || '',
        number: invoice.number,
        // Use customer data from invoice or fallback to customer_id
        display_name: invoice.display_name,
        first_name: invoice.first_name,
        last_name: invoice.last_name,
        company_name: invoice.company_name,
        email: invoice.email,
        billing_addr_line1: invoice.billing_addr_line1,
        billing_addr_city: invoice.billing_addr_city,
        billing_addr_state: invoice.billing_addr_state,
        billing_addr_zip: invoice.billing_addr_zip,
        issue_date: invoice.issue_date,
        due_date: invoice.due_date,
        status: invoice.status,
        sub_total: invoice.sub_total || invoice.total_amount || 0,
        tax_total: invoice.tax_total || 0,
        balance_due: invoice.balance_due || invoice.total_amount || 0,
        items: (invoice as any).items || [],
        total_amount: invoice.total_amount || invoice.sub_total,
      };
    });
  }, [invoices]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      
      await axios.delete(`${API_BASE_URL}/invoices/${id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      // Refresh the list
      mutate();
    } catch (err: any) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/invoices/${id}/edit`);
  };

  const handleView = (id: string) => {
    navigate(`/invoices/${id}`);
  };

  const handleDownload = (id: string) => {
    // Download functionality will be handled by InvoiceActions component
    console.log('Download invoice:', id);
  };

  const handleSend = (id: string) => {
    // Send functionality will be handled by InvoiceActions component
    console.log('Send invoice:', id);
  };

  const handleDuplicate = (id: string) => {
    // Duplicate functionality will be handled by InvoiceActions component
    console.log('Duplicate invoice:', id);
  };

  const handleOpenWizard = (customerId?: string) => {
    setModalCustomerId(customerId);
    setIsWizardModalOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardModalOpen(false);
    setModalCustomerId(undefined);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* container */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Invoices</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage and track your invoices
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => navigate('/invoices/mass-create')}
              aria-label="Mass Create Invoices"
              className="rounded-xl bg-green-600 text-white px-4 py-2 text-sm font-semibold hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-600 transition-colors"
            >
              <FiPlus className="mr-2 h-4 w-4" />
              Mass Create
            </button>
            <button
              type="button"
              onClick={() => handleOpenWizard()}
              aria-label="Create Invoice"
              className="rounded-xl bg-sedra text-white px-4 py-2 text-sm font-semibold hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra transition-colors"
            >
              <FiPlus className="mr-2 h-4 w-4" />
              Create Invoice
            </button>
          </div>
        </div>

        {/* Error Message */}
        {fetchError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-red-800 dark:text-red-200">
              {fetchError.message || 'Failed to load invoices'}
            </p>
          </div>
        )}

        {/* Invoice Table */}
        <InvoiceTable
          invoices={transformedInvoices}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onDownload={handleDownload}
          onSend={handleSend}
          onDuplicate={handleDuplicate}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        {/* Load More Button */}
        {!isReachingEnd && !isEmpty && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Empty State */}
        {isEmpty && !isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500">
              <p className="text-lg font-medium">No invoices found</p>
              <p className="text-sm mt-2">Create your first invoice to get started</p>
            </div>
          </div>
        )}

        {/* Invoice Wizard Modal */}
        <InvoiceFormWizard
          isOpen={isWizardModalOpen}
          onClose={handleCloseWizard}
          onSuccess={() => {
            console.log('Invoice created successfully, refreshing list...');
            handleCloseWizard();
            console.log('Calling mutate...');
            mutate().then(() => {
              console.log('Mutate completed');
            }).catch((err) => {
              console.error('Mutate failed:', err);
            });
          }}
          customerId={modalCustomerId}
        />
      </div>
    </div>
  );
};

export default InvoicesPage; 